import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crop, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// Charge une carte (PDF ou image) et la dessine dans le canvas ; renvoie le nombre de pages.
async function renderChart(canvas, chart, pageNo, cache) {
  if (!cache.current[chart.id]) {
    const { data } = await api.get(`/vac-charts/${chart.id}/download`, { responseType: "arraybuffer" });
    cache.current[chart.id] = chart.content_type?.startsWith("image/")
      ? { img: await createImageBitmap(new Blob([data], { type: chart.content_type })) }
      : { pdf: await pdfjs.getDocument({ data }).promise };
  }
  const src = cache.current[chart.id];
  const ctx = canvas.getContext("2d");
  if (src.img) {
    canvas.width = src.img.width;
    canvas.height = src.img.height;
    ctx.drawImage(src.img, 0, 0);
    return 1;
  }
  const page = await src.pdf.getPage(pageNo);
  const vp = page.getViewport({ scale: 2.5 });
  canvas.width = vp.width;
  canvas.height = vp.height;
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return src.pdf.numPages;
}

export default function VacCaptureDialog({ open, onOpenChange, charts, targets, briefingId, onSaved }) {
  const canvasRef = useRef(null);
  const cache = useRef({});
  const [chartId, setChartId] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const drag = useRef(null);
  const chart = charts.find((c) => c.id === chartId) || charts[0];

  useEffect(() => {
    if (!open || !chart) { return; }
    setBusy(true);
    setSel(null);
    const t = setTimeout(() => {
      if (!canvasRef.current) { return; }
      renderChart(canvasRef.current, chart, page, cache)
        .then(setPages)
        .catch(() => toast.error("Impossible d'afficher la carte"))
        .finally(() => setBusy(false));
    }, 50);
    return () => clearTimeout(t);
  }, [open, chart, page]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: Math.min(Math.max(e.clientX - r.left, 0), r.width), y: Math.min(Math.max(e.clientY - r.top, 0), r.height) };
  };
  const onDown = (e) => { e.preventDefault(); drag.current = pos(e); setSel(null); };
  const onMove = (e) => {
    if (!drag.current) { return; }
    const p = pos(e), s = drag.current;
    setSel({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = () => { drag.current = null; };

  const save = async (pointId) => {
    const cv = canvasRef.current;
    const k = cv.width / cv.getBoundingClientRect().width;
    const out = document.createElement("canvas");
    out.width = Math.round(sel.w * k);
    out.height = Math.round(sel.h * k);
    out.getContext("2d").drawImage(cv, sel.x * k, sel.y * k, out.width, out.height, 0, 0, out.width, out.height);
    const blob = await new Promise((r) => out.toBlob(r, "image/png"));
    const fd = new FormData();
    fd.append("file", new File([blob], `ATIS_${chart.icao}.png`, { type: "image/png" }));
    fd.append("point_id", pointId);
    try {
      await api.post(`/briefings/${briefingId}/files`, fd);
      toast.success("Capture ajoutée au cadre ATIS du log Excel");
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error("Échec de l'enregistrement de la capture");
    }
  };

  const ok = sel && sel.w > 8 && sel.h > 8;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vac-capture-dialog" className="max-h-[94vh] max-w-5xl overflow-y-auto border-slate-700 bg-[#0B1222] text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-cyan-300"><Crop className="h-4 w-4" /> Capturer une zone de carte VAC</DialogTitle>
          <DialogDescription className="text-slate-400">Encadrez à la souris (ou au doigt) la zone utile, puis envoyez-la dans le cadre ATIS du log Excel.</DialogDescription>
        </DialogHeader>
        {charts.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="vac-capture-empty">Aucune carte VAC sélectionnée dans ce briefing ou présente dans la bibliothèque.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {charts.map((c) => (
                <button key={c.id} onClick={() => { setChartId(c.id); setPage(1); }} data-testid={`vac-capture-chart-${c.icao}`}
                  className={`rounded-md border px-3 py-1 font-mono-data text-sm ${chart?.id === c.id ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-300" : "border-slate-700 text-slate-400"}`}>
                  {c.icao}
                </button>
              ))}
              {pages > 1 && (
                <div className="ml-auto flex items-center gap-1 text-xs text-slate-400">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded p-1 hover:bg-slate-800 disabled:opacity-30" data-testid="vac-capture-prev"><ChevronLeft className="h-4 w-4" /></button>
                  Page {page} / {pages}
                  <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded p-1 hover:bg-slate-800 disabled:opacity-30" data-testid="vac-capture-next"><ChevronRight className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="relative mx-auto w-fit max-w-full select-none overflow-hidden rounded-lg border border-slate-700 bg-white" style={{ touchAction: "none" }}
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} data-testid="vac-capture-area">
              <canvas ref={canvasRef} className="mx-auto block h-auto max-h-[65vh] w-auto max-w-full" />
              {sel && <div className="pointer-events-none absolute border-2 border-cyan-500 bg-cyan-400/20" style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }} />}
              {busy && <div className="absolute inset-0 flex items-center justify-center bg-slate-950/40"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>}
            </div>
            <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 bg-[#0B1222] py-2">
              {targets.map((t) => (
                <Button key={t.pointId} disabled={!ok} onClick={() => save(t.pointId)} data-testid={`vac-capture-save-${t.pointId.split("::")[1]}`}
                  className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
                  Utiliser pour {t.label}
                </Button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
